import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'

export type Role = 'superadmin' | 'admin' | 'company' | 'autonomo' | 'owner' | 'employee' | 'agency'
export type ProfileStatus = 'pending' | 'approved' | 'rejected'

export interface Profile {
  id: string
  email: string
  role: Role
  status: ProfileStatus
  locale: string | null
  rejection_reason?: string | null
  blocked?: boolean
  blocked_reason?: string | null
  blocked_at?: string | null
  /** Role-detail rows (0..1 each) via the profile_id relationships. */
  companies?: { legal_name: string; cif: string; address: Record<string, unknown> }[] | null
  autonomos?: { full_name: string; nif: string; irpf_pct: number; address: Record<string, unknown> }[] | null
  owners?: { full_name: string; nif_cif: string; iban: string }[] | null
}

/** The human-facing identity for the signed-in account (company legal name or person). */
export function accountName(p: Profile | null): string {
  if (!p) return ''
  return (
    p.companies?.[0]?.legal_name ||
    p.autonomos?.[0]?.full_name ||
    p.owners?.[0]?.full_name ||
    p.email
  )
}

/** Localized role label. Moved here from the (now-deleted) Dashboard summary
 *  page so Profile can keep using it without that page existing. */
export function roleLabel(role: Role | undefined, t: (k: string) => string): string {
  switch (role) {
    case 'company':
      return t('auth.roleCompany')
    case 'autonomo':
      return t('auth.roleAutonomo')
    case 'owner':
      return t('auth.roleOwner')
    case 'employee':
      return t('auth.roleEmployeeLabel')
    case 'agency':
      return t('auth.roleAgencyLabel')
    case 'admin':
    case 'superadmin':
      return t('admin.roleAdmin')
    default:
      return role ?? ''
  }
}

interface AuthState {
  session: Session | null
  profile: Profile | null
  loading: boolean
  /** True when a session exists but the profile row couldn't be loaded. */
  profileError: boolean
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    profile: null,
    loading: true,
    profileError: false,
  })

  const loadProfile = async (session: Session | null) => {
    if (!session?.user) {
      setState({ session: null, profile: null, loading: false, profileError: false })
      return
    }
    const { data, error } = await supabase
      .from('profiles')
      .select(
        'id, email, role, status, locale, rejection_reason, companies(legal_name, cif, address), autonomos(full_name, nif, irpf_pct, address), owners(full_name, nif_cif, iban)',
      )
      .eq('id', session.user.id)
      .single()
    if (error || !data) {
      // Profile row is seeded by a DB trigger on auth.users; if missing the
      // trigger hasn't run yet (or env isn't configured). Surface a logged-out
      // state rather than blocking the whole app.
      setState({ session, profile: null, loading: false, profileError: true })
      return
    }
    setState({ session, profile: data as Profile, loading: false, profileError: false })
  }

  useEffect(() => {
    // Both getSession() and the INITIAL_SESSION event deliver the boot state;
    // whichever arrives first wins (ref-guarded) so we don't fetch twice.
    const bootRef = { current: false }
    const boot = (session: Session | null) => {
      if (bootRef.current) return
      bootRef.current = true
      void loadProfile(session)
    }
    // getSession may reject when no backend is configured (placeholder URL);
    // swallow so the UI still renders a logged-out state.
    supabase.auth
      .getSession()
      .then(({ data }) => boot(data.session))
      .catch(() => boot(null))

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') boot(session)
      else void loadProfile(session)
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  // While the account is not approved yet (or the profile row hasn't appeared),
  // poll so an admin approval flips the UI without a manual reload. Stops as
  // soon as the profile is approved (or the session goes away).
  const awaitingApproval = Boolean(
    state.session && !state.loading && (!state.profile || state.profile.status !== 'approved'),
  )
  useEffect(() => {
    if (!awaitingApproval || !state.session) return
    const id = setInterval(() => {
      void loadProfile(state.session)
    }, 5000)
    return () => clearInterval(id)
  }, [awaitingApproval, state.session])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signOut: async () => {
        await supabase.auth.signOut()
        setState({ session: null, profile: null, loading: false, profileError: false })
      },
      refreshProfile: async () => {
        await loadProfile(state.session)
      },
    }),
    [state],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}